/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, {
	type KeyboardEventHandler,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { cssMap, jsx } from '@compiled/react';
import debounce from 'lodash/debounce';
import Mousetrap from 'mousetrap';
import { defineMessages, useIntl } from 'react-intl-next';

import type { DocNode } from '@atlaskit/adf-schema';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { IconButton } from '@atlaskit/button/new';
import { editorCardProvider } from '@atlaskit/editor-card-provider';
import { INPUT_METHOD } from '@atlaskit/editor-common/analytics';
import {
	sharedPluginStateHookMigratorFactory,
	useSharedPluginState,
} from '@atlaskit/editor-common/hooks';
import type { ExtractPresetAPI } from '@atlaskit/editor-common/preset';
import { useSharedPluginStateSelector } from '@atlaskit/editor-common/use-shared-plugin-state-selector';
import { isEmptyDocument, toJSON } from '@atlaskit/editor-common/utils';
import { ComposableEditor } from '@atlaskit/editor-core/composable-editor';
import { EditorContext } from '@atlaskit/editor-core/editor-context';
import { usePreset } from '@atlaskit/editor-core/use-preset';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import LinkIcon from '@atlaskit/icon/core/link';
import MentionIcon from '@atlaskit/icon/core/mention';
import SendIcon from '@atlaskit/icon/core/send';
import { Inline } from '@atlaskit/primitives/compiled';
import { TextSerializer } from '@atlaskit/renderer/text-serializer';
import { token } from '@atlaskit/tokens';
import { type SurfaceMetadata } from '@atlassian/ai-smart-button-api';
import { useLinkPickerEditorProps } from '@atlassian/link-picker-plugins/editor';
import { SmartMentionResource } from '@atlassian/smart-mention-resource';

import { createAIPromptPreset } from './preset';

export const i18n = defineMessages({
	inputPlaceholder: {
		id: 'ai-smart-button.ai-actions.input.placeholder',
		defaultMessage: 'Type what you need',
		description:
			'Placeholder for the input which allows users to query AI actions or submit custom queries.',
	},
	linkPickerButtonLabel: {
		id: 'ai-smart-button.ai-actions.input.link-picker-button-label',
		defaultMessage: 'Link',
		description: 'Label for the link picker button',
	},
	mentionButtonLabel: {
		id: 'ai-smart-button.ai-actions.input.mention-button-label',
		defaultMessage: 'Mention',
		description: 'Label for the mention button',
	},
	sendButtonLabel: {
		id: 'ai-smart-button.ai-actions.input.send-button-label',
		defaultMessage: 'Send',
		description: 'Label for the send button',
	},
});

const styles = cssMap({
	container: {
		borderTopColor: token('color.border'),
		borderTopStyle: 'solid',
		borderWidth: token('border.width'),
	},
	hiddenCaret: {
		caretColor: 'transparent',
	},
	editor: {
		display: 'flex',
		flexGrow: 1,
	},
	fullWidthEditor: {
		width: '100%',
	},
	/**
	 * We use absolute position to make the Editor full width
	 * as the link picker opens at the end of the Editor,
	 * and making the Editor smaller would push the link picker modal
	 * to be cut off by object sidebar boundaries.
	 */
	buttonsContainer: {
		position: 'absolute',
		right: token('space.150'),
	},
	prosemirrorOverrides: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'div > .ProseMirror': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			'> p': {
				// We need a tiny bit of padding to avoid inline nodes with borders being visually clipped
				// and wwe need to increase the line height to allow for when inline nodes are focused as
				// they add box-shadow styling of 1px
				// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
				paddingTop: '0.5px',
			},

			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
			'> p:last-child': {
				marginBottom: 0,
				paddingBottom: 0,
			},

			/**
			 * Minimum height is set to 125px for confluence inline comment editor (ProseMirror element)
			 * When Editor AI is invoked in inline comments, same css rule applies here as well.
			 * So overriding it here.
			 */
			minHeight: 'auto',
		},

		// Smart link's inline card view has 0.5em padding (7px or 8px)
		// Because of that scrollbar appearts in prompt input because of limited height available.
		// More details here https://product-fabric.atlassian.net/browse/EDF-455
		// So removed this top and bottom padding here.
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'.ProseMirror .inlineCardView-content-wrap .card': {
			paddingTop: 0,
			paddingBottom: 0,
		},
	},
	promptInput: {
		position: 'relative',
		flexShrink: 0,
		display: 'flex',
		flexWrap: 'wrap',
		gap: token('space.100'),
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
		borderRadius: token('border.radius'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		marginLeft: token('space.300'),
		marginRight: token('space.300'),
		marginTop: token('space.150'),
		marginBottom: token('space.150'),
		borderColor: token('color.border.input'),
	},
	focused: {
		borderColor: token('color.border.focused'),
	},
});

// Extract the API type to avoid self-referencing in the parameter type
type AIPromptPresetAPI = ExtractPresetAPI<ReturnType<typeof createAIPromptPreset>> | undefined;

const useSharedState = sharedPluginStateHookMigratorFactory(
	(api: AIPromptPresetAPI) => {
		const mentionsTypeAheadHandler = useSharedPluginStateSelector(api, 'mention.typeAheadHandler');
		const hasFocus = useSharedPluginStateSelector(api, 'focus.hasFocus');
		const activeLinkMark = useSharedPluginStateSelector(api, 'hyperlink.activeLinkMark');
		const typeAheadCurrentHandler = useSharedPluginStateSelector(api, 'typeAhead.currentHandler');
		return {
			hasFocus,
			activeLinkMark,
			mentionTypeAheadHandlerId: typeAheadCurrentHandler?.id,
			typeAheadCurrentHandlerId: mentionsTypeAheadHandler?.id,
		};
	},
	(api: AIPromptPresetAPI) => {
		const { focusState, mentionState, hyperlinkState, typeAheadState } = useSharedPluginState(api, [
			'focus',
			'mention',
			'hyperlink',
			'typeAhead',
		]);
		return {
			mentionTypeAheadHandlerId: mentionState?.typeAheadHandler?.id,
			typeAheadCurrentHandlerId: typeAheadState?.currentHandler?.id,
			hasFocus: focusState?.hasFocus,
			activeLinkMark: hyperlinkState?.activeLinkMark,
		};
	},
);

export function AIInput({
	onADFChange,
	onInputChange,
	onSelect,
	handleArrowDown,
	handleArrowUp,
	handleBack,
	surfaceMetadata,
}: {
	onADFChange?: (adf: JSONDocNode) => void;
	onInputChange?: (text: string) => void;
	onSelect?: (adf: DocNode) => void;
	handleArrowDown?: () => void;
	handleArrowUp?: () => void;
	handleBack?: () => void;
	surfaceMetadata?: SurfaceMetadata;
}) {
	const intl = useIntl();
	const onSaveRef = useRef<() => void>();

	const mousetrapRef = useRef<Mousetrap.MousetrapInstance | null>(null);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const mentionResource = useMemo(() => {
		if (!surfaceMetadata) {
			return undefined;
		}
		return Promise.resolve(
			new SmartMentionResource({
				productKey: surfaceMetadata.product,
				siteId: surfaceMetadata.siteId,
				includeTeams: true,
				includeUsers: true,
			}),
		);
	}, [surfaceMetadata]);

	const linkPicker = useLinkPickerEditorProps(undefined, {
		cloudId: surfaceMetadata?.siteId,
		product: surfaceMetadata?.product,
	});

	const { preset, editorApi } = usePreset(
		() =>
			createAIPromptPreset({
				placeholder: {
					placeholder: intl.formatMessage(i18n.inputPlaceholder),
				},
				linkPicker,
				cardProvider: Promise.resolve(editorCardProvider),
				mentionProvider: mentionResource,
				onSave: () => onSaveRef.current?.(),
			}),
		[intl],
	);

	const handleSelect = useCallback(() => {
		if (onSelect) {
			editorApi?.core.actions.requestDocument((adf) => onSelect(adf as DocNode));
		}
	}, [onSelect, editorApi]);

	useEffect(() => {
		onSaveRef.current = handleSelect;
	}, [handleSelect]);

	const { mentionTypeAheadHandlerId, typeAheadCurrentHandlerId, activeLinkMark, hasFocus } =
		useSharedState(editorApi);

	const currentTypeAhead = useMemo(() => {
		if (typeAheadCurrentHandlerId === 'quickInsertTypeAhead') {
			return 'quickInsert';
		}
		if (typeAheadCurrentHandlerId === mentionTypeAheadHandlerId) {
			return 'mention';
		}

		if (!!activeLinkMark) {
			return 'link';
		}
	}, [activeLinkMark, mentionTypeAheadHandlerId, typeAheadCurrentHandlerId]);

	const [isInputEmpty, setIsInputEmpty] = useState(true);

	const handleChange = useCallback(
		(editorView: EditorView) => {
			if (isEmptyDocument(editorView.state.doc)) {
				setIsInputEmpty(true);
			} else {
				setIsInputEmpty(false);
			}

			if (onInputChange) {
				const textSerializer = TextSerializer.fromSchema(editorView.state.schema);
				const text = textSerializer.serializeFragment(editorView.state.doc.content);
				onInputChange(text);
			}

			if (onADFChange) {
				const adf = toJSON(editorView.state.doc);
				onADFChange(adf);
			}
		},
		[onADFChange, onInputChange],
	);

	const setMousetrapRef = useCallback((node: any) => {
		mousetrapRef.current?.reset();
		if (!node) {
			return;
		}
		mousetrapRef.current = new Mousetrap(node);
	}, []);

	/**
	 * Prevent Editor keystrokes from triggering global shortcuts.
	 * Could potentially move this logic outside of platform later.
	 */
	// Prevent editor key presses from triggering global shortcuts
	const suppressGlobalShortcuts: KeyboardEventHandler<HTMLDivElement> = useCallback((event) => {
		mousetrapRef.current?.bind(event.key.toLocaleLowerCase(), (e) => e.stopPropagation());
	}, []);

	const [isCaretHidden, setIsCaretHidden] = useState(false);

	const debouncedSetIsCaretHidden = useMemo(
		() =>
			debounce(() => {
				setIsCaretHidden(false);
			}, 300),
		[],
	);

	const handleKeyUp = useCallback(() => {
		debouncedSetIsCaretHidden();
	}, [debouncedSetIsCaretHidden]);

	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent) => {
			if (currentTypeAhead) {
				// Prevent this handler from interacting when typeahead is open
				return;
			}
			switch (event.key) {
				case 'ArrowUp': {
					setIsCaretHidden(true);
					handleArrowUp?.();
					event.preventDefault();
					event.stopPropagation();
					break;
				}
				case 'ArrowDown': {
					setIsCaretHidden(true);
					handleArrowDown?.();
					event.preventDefault();
					event.stopPropagation();
					break;
				}
				case 'Backspace': {
					if (isInputEmpty) {
						event.preventDefault();
						event.stopPropagation();
						handleBack?.();
					}
					break;
				}
				case 'Escape': {
					event.preventDefault();
					event.stopPropagation();
					handleBack?.();
					break;
				}
				default: {
					return;
				}
			}
		},
		[handleArrowDown, handleArrowUp, handleBack, isInputEmpty, currentTypeAhead],
	);

	const closeTypeAhead = useCallback(() => {
		editorApi?.typeAhead?.actions.close({ insertCurrentQueryAsRawText: false });
	}, [editorApi]);

	const openMentionTypeAhead = useCallback(() => {
		closeTypeAhead();
		editorApi?.mention?.actions.openTypeAhead(INPUT_METHOD.QUICK_INSERT);
	}, [closeTypeAhead, editorApi]);

	const openLinkTypeAhead = useCallback(() => {
		closeTypeAhead();
		const command = editorApi?.hyperlink?.commands.showLinkToolbar(INPUT_METHOD.QUICK_INSERT);
		editorApi?.core.actions.execute(command);
	}, [closeTypeAhead, editorApi]);

	const handleOnClickAnalytics = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: surfaceMetadata!.contentType,
				action: 'clicked',
				actionSubject: 'rovoButton',
				actionSubjectId: 'chatInput',
				attributes: {
					product: surfaceMetadata!.product,
					productPage: surfaceMetadata!.contentType,
					pageMode: surfaceMetadata!.pageMode,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, surfaceMetadata]);

	return (
		<EditorContext>
			{/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
			<div ref={setMousetrapRef} onKeyDown={suppressGlobalShortcuts} css={[styles.container]}>
				<div
					css={[
						styles.promptInput,
						hasFocus && styles.focused,
						isCaretHidden && styles.hiddenCaret,
					]}
				>
					{/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, @atlassian/a11y/interactive-element-not-keyboard-focusable */}
					<div
						onKeyUp={handleKeyUp}
						onKeyDown={handleKeyDown}
						css={[styles.editor, styles.prosemirrorOverrides, styles.fullWidthEditor]}
						onClick={handleOnClickAnalytics}
					>
						<ComposableEditor
							mentionProvider={mentionResource}
							shouldFocus={true}
							preset={preset}
							maxHeight={160}
							appearance="chromeless"
							minHeight={20}
							onChange={handleChange}
						/>
					</div>
					<Inline
						space="space.050"
						alignInline="end"
						spread={linkPicker || mentionResource ? 'space-between' : undefined}
						alignBlock="end"
						grow={isInputEmpty ? 'hug' : 'fill'}
						xcss={isInputEmpty && styles.buttonsContainer}
					>
						<Inline space="space.050">
							{linkPicker && (
								<IconButton
									appearance="subtle"
									spacing="compact"
									label={intl.formatMessage(i18n.linkPickerButtonLabel)}
									icon={LinkIcon}
									onMouseDown={(e) => e.preventDefault()}
									isSelected={currentTypeAhead === 'link'}
									onClick={openLinkTypeAhead}
								/>
							)}
							{mentionResource && (
								<IconButton
									appearance="subtle"
									spacing="compact"
									label={intl.formatMessage(i18n.mentionButtonLabel)}
									icon={MentionIcon}
									onMouseDown={(e) => e.preventDefault()}
									isSelected={currentTypeAhead === 'mention'}
									onClick={openMentionTypeAhead}
								/>
							)}
						</Inline>
						{!isInputEmpty && (
							<IconButton
								appearance="subtle"
								spacing="compact"
								onClick={handleSelect}
								type="submit"
								icon={SendIcon}
								label={intl.formatMessage(i18n.sendButtonLabel)}
							/>
						)}
					</Inline>
				</div>
			</div>
		</EditorContext>
	);
}
